import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  getInfoDashboardNbOfRequest,
  getUserDashboardData,
  getUserDashboardDataCount,
  getUserDashboardRelaunch,
  userClientAssignment,
  userPartnerAssignment,
} from "../../services/Api/userService";
import { useDashboardStore } from "../../stores/Layout/dashboard";
import "../../css/animations.css";
import SideBar from "../../components/Layout/SideBar";
import { ToastContainer } from "react-toastify";
import "../../css/carousel.css";
import CarouselDashboard from "../../components/Dashboard/CarouselDashboard";
import Header from "../../components/Layout/Header";
import Footer from "../../components/Layout/Footer";
import FollowMission from "../../components/Dashboard/FollowMission";
import Counts from "../../components/Dashboard/Counts";
import Request from "../../components/Dashboard/Request";
import Relaunch from "../../components/Dashboard/Relaunch";
import Information from "../../components/Dashboard/Information";
import { showToastError } from "../../services/Toasts/toasts";
import { getLatestDocumentTimestamp } from "../../services/Utils/functionService";
import { requestDesktopNotificationPermission } from "../../services/Utils/browserNotification";

const Dashboard = () => {
  const { t } = useTranslation();
  const {
    setCardsPartner,
    setCardsCustomer,
    setDataCount,
    setRequestsInfo,
    setFollowMissions,
    setRelaunchsInfo,
  } = useDashboardStore();

  // Ajout des assignations aux cartes
  useEffect(() => {
    userClientAssignment()
      .then((response) => setCardsCustomer(response))
      .catch(() => showToastError(t("dashboard.error")));

    userPartnerAssignment()
      .then((response) => setCardsPartner(response))
      .catch(() => showToastError(t("dashboard.error")));

    getUserDashboardData()
      .then((response) => {
        // Plus recent en premier
        const sortedMissionsByDate = response.sort((a: any, b: any) => {
          const dateA = getLatestDocumentTimestamp(a.latestDocumentUploaded);
          const dateB = getLatestDocumentTimestamp(b.latestDocumentUploaded);
          return dateB - dateA;
        });
        setFollowMissions(sortedMissionsByDate);
      })
      .catch(() => showToastError(t("dashboard.error")));

    getInfoDashboardNbOfRequest()
      .then((data) => setRequestsInfo(data))
      .catch(() => showToastError(t("dashboard.error")));

    getUserDashboardRelaunch()
      .then((data) => setRelaunchsInfo(data))
      .catch(() => showToastError(t("dashboard.error")));
  }, []);

  useEffect(() => {
    getUserDashboardDataCount()
      .then((data) => setDataCount(data))
      .catch(() => showToastError(t("dashboard.error")));

    requestDesktopNotificationPermission();
  }, []);

  return (
    <div className="min-h-dvh w-full bg-gradient-to-br from-slate-50 to-blue-50">
      <ToastContainer position="top-center" />

      {/* Structure mobile : navigation complète, carrousel comme unique widget. */}
      <div className="flex h-dvh w-full flex-col overflow-hidden sm:hidden">
        <Header />
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <SideBar />
          <main className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto pb-14">
            <div className="mx-3 h-[min(72dvh,30rem)] min-h-72 w-full overflow-hidden rounded-2xl border border-gray-200/80 bg-white/95 p-2 shadow-xl backdrop-blur-sm">
              <CarouselDashboard />
            </div>
          </main>
        </div>
        <Footer />
      </div>

      {/* Dashboard complet à partir du format tablette. */}
      <div className="hidden h-dvh w-full flex-col overflow-hidden sm:flex">
        <Header />
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <SideBar />
          <main className="flex min-h-0 flex-1 flex-col overflow-hidden pb-16 pl-14 pt-2 md:pb-20">
            <div className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col overflow-hidden px-3 lg:px-4 2xl:max-w-[2400px]">
            <div className="mb-2 flex-shrink-0">
              <Counts />
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden xl:flex-row">
              {/* Colonne gauche */}
              <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden xl:w-2/3">
                {/* Affichage des cards entreprises carousel */}
                <section className="flex min-h-0 flex-[2] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-3 shadow-md lg:p-4">
                  <div className="mb-2 flex flex-shrink-0 items-center gap-3">
                    <div className="rounded-xl bg-yellow-trio p-2.5">
                      <svg
                        className="h-5 w-5 text-grey-trio md:h-6 md:w-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h1 className="font-K2D text-lg font-bold text-gray-700 md:text-xl">
                        {t("dashboard.companiesTitle")}
                      </h1>
                      <p className="mt-0.5 text-sm text-gray-600">
                        {t("dashboard.companiesSubtitle")}
                      </p>
                    </div>
                  </div>
                  <div className="flex-1 min-h-0 overflow-hidden">
                    <CarouselDashboard />
                  </div>
                </section>
                {/* Request et Relaunch */}
                <div className="hidden min-h-0 flex-[1.5] gap-3 overflow-hidden xl:flex">
                  <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md">
                    <Request />
                  </section>
                  <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md">
                    <Relaunch />
                  </section>
                </div>
              </div>

              {/* Colonne droite */}
              <aside className="hidden flex-col gap-3 overflow-hidden xl:flex xl:w-1/3 xl:flex-shrink-0">
                <section className="flex min-h-0 flex-[4] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-2 shadow-md">
                  <FollowMission />
                </section>
                <section className="flex min-h-0 flex-[1] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-2 shadow-md">
                  <Information />
                </section>
              </aside>
            </div>
          </div>
          </main>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default Dashboard;
